<template>
  <mia-car-edit-page-header>
    <div
      class="relative pt-10"
      :class="{ 'pb-10': !hasActions }"
    >
      <mia-flags-preview
        v-if="car.flags && car.flags.length > 0"
        :flags="car.flags"
        class="absolute top-0 left-0 pt-4 pl-4"
      />

      <div>
        <div class="px-16 text-center">
          <p class="mb-0 font-bold text-gray-900">
            {{ car.item_mobile_make }} {{ car.item_mobile_model }}
          </p>
          <p class="mt-2 mb-0 text-lg font-bold text-gray-700">
            {{ car.item_shared_title || 'Ohne Fahrzeugtitel' }}
          </p>
        </div>
      </div>
      <div
        v-if="!collapsed"
        class="flex flex-col items-center mt-4 xl:flex-row xl:items-start"
      >
        <div class="flex justify-center flex-1 xl:pt-12 xl:justify-end">
          <div class="flex flex-col">
            <div>
              <div class="flex flex-col">
                <mia-car-edit-labelled-text label="ID">
                  {{ car.id }}
                </mia-car-edit-labelled-text>
                <mia-car-edit-labelled-text
                  label="FIN"
                  class="mt-4"
                >
                  <span
                    v-clipboard="car.item_fin"
                    v-clipboard:success="notifyCopyFinToClipboard"
                    class="text-xl cursor-pointer hover:bg-gray-400"
                    v-text="car.item_fin"
                  />
                </mia-car-edit-labelled-text>

                <mia-car-edit-labelled-text
                  class="mt-4"
                  label="Kommissionsnummer"
                >
                  <span
                    v-clipboard="car.item_commission_number"
                    v-clipboard:success="notifyCopyCommissionNumberToClipboard"
                    class="text-xl cursor-pointer hover:bg-gray-400"
                    v-text="car.item_commission_number"
                  />
                </mia-car-edit-labelled-text>
              </div>

              <transition
                enter-class="opacity-0"
                enter-active-class="transition-opacity duration-500 ease-out-fast"
                enter-to-class="opacity-100"
                leave-class="opacity-100"
                leave-active-class="transition-opacity duration-500 ease-out-fast"
                leave-to-class="opacity-0"
              >
                <div
                  v-if="clipboardNotification"
                  class="absolute px-4 py-2 -mt-10 -ml-2 text-white rounded-full shadow-xl bg-orange-mia"
                >
                  {{ clipboardNotification }} kopiert!
                </div>
              </transition>
            </div>
            <mia-car-edit-labelled-text
              label="Kategorie"
              class="mt-4"
            >
              {{ car.item_type }}
            </mia-car-edit-labelled-text>
          </div>
        </div>

        <mia-car-edit-overall-progress
          class="my-16 xl:mx-20"
          :progress="getOverallProgress()"
        />

        <div class="flex justify-center flex-1 xl:pt-12 xl:justify-start">
          <div class="flex flex-col">
            <mia-car-edit-labelled-text label="Status">
              <mia-car-edit-status
                :status="car.item_status"
                :vti-status="car.item_vti_status"
              />
            </mia-car-edit-labelled-text>

            <mia-car-edit-labelled-text
              v-if="car.seller_display_name"
              label="Verkäufer"
              class="mt-4"
            >
              <a
                :href="`/user/profile/${car.item_sold_seller}`"
              >
                {{ car.seller_display_name }}
              </a>
            </mia-car-edit-labelled-text>

            <mia-car-edit-labelled-text
              v-if="car.customer_display_name"
              label="Käufer"
              class="mt-4"
            >
              <a
                :href="`/legacy/customer_edit/${car.item_customer}`"
              >
                {{ car.customer_display_name }}
              </a>
            </mia-car-edit-labelled-text>

            <mia-car-edit-labelled-text
              label="Lagerplatz"
              class="mt-4"
            >
              <a
                v-if="stockSlotId"
                :href="`/legacy/stock_edit/${stockSlotId}`"
                target="_blank"
              >
                {{ stockSlotLabel }}
              </a>
              <span v-else>-</span>
            </mia-car-edit-labelled-text>

            <mia-car-edit-labelled-text
              label="Endkundenpreis"
              class="mt-4"
            >
              <template v-if="car.item_price_customer">
                <span>{{ car.item_price_customer | price }} <small class="text-sm">{{ car.item_tax === 'default' ? 'Netto' : '' }}</small></span>
                <span
                  v-if="car.item_tax === 'default'"
                  class="block mt-1 text-base"
                >
                  {{ car.item_price_customer | grossPrice }} <small class="text-sm">Brutto</small>
                </span>
              </template>
              <template v-else>
                -
              </template>
            </mia-car-edit-labelled-text>

            <mia-car-edit-labelled-text
              label="Aktionspreis"
              class="mt-4"
            >
              <template v-if="car.offer_prices">
                <span>{{ car.offer_prices.item_price_customer | price }} <small class="text-sm">Netto</small></span>
                <span class="block mt-1 text-base">{{ car.offer_prices.item_price_customer | grossPrice }} <small class="text-sm">Brutto</small></span>
              </template>
              <template v-else>
                -
              </template>
            </mia-car-edit-labelled-text>

            <mia-car-exchange-info
              class="mt-4"
              @exchange-saved="onExchangeSaved"
            />
          </div>
        </div>
      </div>

      <div
        v-if="!collapsed"
        class="w-full max-w-xl mx-auto mt-6"
      >
        <mia-car-edit-quick-search />
      </div>

      <div
        v-if="collapsible"
        class="absolute left-0 bottom-0 pl-4 pb-2"
      >
        <button
          type="button"
          class="text-gray-900 hover:text-orange-mia focus:outline-none"
          @click="$emit('update:collapsed', !collapsed)"
        >
          <mia-streamline-icon
            class="w-5 h-5 fill-current"
            :src="collapsed ? 'arrow-down-1' : 'arrow-up-1'"
          />
        </button>
      </div>
    </div>

    <div
      v-if="hasActions"
      class="flex justify-end p-4 mt-6 bg-gray-300"
    >
      <slot name="actions" />
    </div>
  </mia-car-edit-page-header>
</template>

<script>
import get from 'lodash/get'
import sumBy from 'lodash/sumBy'
import { mapGetters } from 'vuex'
import { getCar } from '@/api/car'
import LabelledText from '@/components/cars/edit/LabelledText.vue'
import PageHeader from '@/components/cars/edit/PageHeader.vue'
import ExchangeInfo from '@/components/cars/edit/ExchangeInfo.vue'
import Status from '@/components/cars/edit/Status.vue'
import OverallProgress from '@/components/cars/edit/OverallProgress.vue'
import CarEditQuickSearch from '@/components/cars/edit/QuickSearch.vue'
import grossPrice from '@/filters/gross-price'
import FlagsPreview from '@/components/cars/flags/FlagsPreview.vue'

export default {
  name: 'CarEditSharedPageHeader',

  components: {
    'mia-car-edit-labelled-text': LabelledText,
    'mia-car-edit-page-header': PageHeader,
    'mia-car-edit-status': Status,
    'mia-car-exchange-info': ExchangeInfo,
    'mia-car-edit-overall-progress': OverallProgress,
    'mia-car-edit-quick-search': CarEditQuickSearch,
    'mia-flags-preview': FlagsPreview
  },

  filters: {
    grossPrice
  },

  props: {
    noDefaultActions: {
      type: Boolean,
      default: false
    },

    collapsible: {
      type: Boolean,
      default: false
    },

    collapsed: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    clipboardNotification: null
  }),

  computed: {
    ...mapGetters('progress', ['progress']),

    car: {
      get() {
        return this.$store.state.car.car
      },

      set(car) {
        this.$store.commit('setCar', car)
      }
    },

    stockSlotId() {
      return this.car.item_stock_slot?.id || null
    },

    stockSlotLabel() {
      // eslint-disable-next-line camelcase
      return this.car.item_stock_slot?.item_title || ''
    },

    hasActions() {
      return this.$slots.actions !== undefined && this.$slots.actions.some(vNode => vNode.tag !== undefined)
    }
  },

  methods: {
    getOverallProgress() {
      const pages = get(this.progress, 'caredit.page', {})
      const summedProgress = sumBy(Object.entries(pages), ([, result]) => result.progress)

      return Math.floor(summedProgress / Object.keys(pages).length)
    },

    notifyCopyToClipboard(text) {
      if (this.clipboardNotificationTimer) {
        clearTimeout(this.clipboardNotificationTimer)
      }

      this.clipboardNotification = text

      this.clipboardNotificationTimer = setTimeout(() => {
        this.clipboardNotification = null
        this.clipboardNotificationTimer = undefined
      }, 2500)
    },

    notifyCopyFinToClipboard() {
      this.notifyCopyToClipboard('FIN')
    },

    notifyCopyCommissionNumberToClipboard() {
      this.notifyCopyToClipboard('Kommissionsnummer')
    },

    requestDatUpdate() {
      mia.car.dat.update(this.car.id)
    },

    async onExchangeSaved() {
      this.car = await getCar({
        carId: this.car.id
      })
    }
  }
}
</script>
